/**
 * The scene-manager module serves as an entry point to all scene-related activities, from initialization
 * to access to objects to anything else.
 *
 * The starter version of the scene manager is adapted from the introductory code provided by three.js.
 */
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  DirectionalLight,
  Object3D,
  Raycaster,
  Vector2,
  Clock
} from 'three'

import Turtle from '../cast/turtle'
import CoreInhabitants from '../cast/coreInhabitants'
import Particles from '../cast/particles'
import JellyFish from '../cast/jellyfish'
import Fish from '../cast/fish'
import LittleFishes from '../cast/littleFishes'

const DEFAULT_ROTATION_RATE = 0.005
const FIN_SPEED = 0.4
const clock = new Clock()
const raycaster = new Raycaster()
const mouse = new Vector2()
let counter = 0
let longCounter = 0
let rotateModifier = 3
let tentacleSway = 0

const createDemoUniverse = ({ fieldOfView, width, height, nearPlane, farPlane }) => {
  const scene = new Scene()
  const camera = new PerspectiveCamera(fieldOfView, width / height, nearPlane, farPlane)

  const renderer = new WebGLRenderer({ alpha: true })
  renderer.setSize(width, height)
  renderer.setClearColor(0x000000, 0.0)

  const coreInhabitants = new CoreInhabitants()
  scene.add(coreInhabitants.group)

  const turtleRotation = new Object3D()
  scene.add(turtleRotation)
  const turtle = new Turtle(0x00ff00)
  turtleRotation.add(turtle.group)
  turtle.group.position.set(1.75, 0, 0)
  turtle.frontRight.rotation.x = 0.6
  turtle.frontLeft.rotation.x = -0.6
  turtle.backRight.rotation.x = -0.25
  turtle.backLeft.rotation.x = 0.25

  const jellyRotation = new Object3D()
  scene.add(jellyRotation)
  const jellyFish = new JellyFish(0xce4a21)
  scene.add(jellyFish.group)
  jellyRotation.add(jellyFish.group)
  jellyFish.group.position.set(-0.9, 0.956, 0)

  const fishRotation = new Object3D()
  scene.add(fishRotation)
  const fish = new Fish(0xffffff)
  fishRotation.add(fish.group)
  fish.group.position.set(5, 0, 5)
  fish.group.rotation.y = -Math.PI
  fish.group.rotation.y += 1

  const littleFishRotation = new Object3D()
  scene.add(littleFishRotation)
  const littleFish1 = new LittleFishes(0x666699)
  littleFish1.material.color.setHex(0x666699)
  littleFish1.material.emissive.setHex(0x666699)
  scene.add(littleFish1.group)
  littleFishRotation.add(littleFish1.group)
  littleFish1.group.position.set(-7.1, -1, 0)
  littleFish1.group.rotation.y -= .32

  const littleFish2 = new LittleFishes(0xffffff)
  littleFish2.material.color.setHex(0xff6600)
  littleFish2.material.emissive.setHex(0x666699)
  scene.add(littleFish2.group)
  littleFishRotation.add(littleFish2.group)
  littleFish2.group.rotation.y = (3 * Math.PI) / 2
  littleFish2.group.position.set(12, 7, -6)

  const littleFish3 = new LittleFishes(0xffffff)
  littleFish2.material.color.setHex(0x6666ff)
  scene.add(littleFish3.group)
  littleFishRotation.add(littleFish3.group)
  littleFish3.group.rotation.y = (3 * Math.PI) / 4 
  littleFish3.group.position.set(-5, 2, 7)

  const particles = new Particles(0x2194ce)
  scene.add(particles.group)

  let lights = []
  lights[0] = new DirectionalLight(0xfffddb, 1)
  lights[0].position.set(1.75, 1.75, 0)
  lights[1] = new DirectionalLight(0x1df7cf, 1)
  lights[1].position.set(2, 0, 1.5)
  lights[2] = new DirectionalLight(0x8200c9, 1)
  lights[2].position.set(-1.75, -1.75, 0)
  scene.add(lights[0])
  scene.add(lights[1])
  scene.add(lights[2])

  let turning = true
  const turn = () => {
    turning = true
  }

  const stop = () => {
    turning = false
  }

  let render = function () {
    requestAnimationFrame(render)

    counter++
    if (counter >= 4/FIN_SPEED) {
      counter = 0
    }

    longCounter++
    if (longCounter >= 135) {
      longCounter = 0
      rotateModifier = 3
    }

    renderer.render(scene, camera)
  }

  function animateFish(finSpeed) {
    fish.tailFin.rotation.x += 0.5*finSpeed
    fish.leftFin.rotation.y += 0.25*finSpeed
    fish.rightFin.rotation.y -= 0.25*finSpeed
    littleFish1.tailFin.rotation.x += 0.3*finSpeed
    littleFish1.leftFin.rotation.y += 0.15*finSpeed
    littleFish1.rightFin.rotation.y -= 0.15*finSpeed
    littleFish2.tailFin.rotation.x += 0.4*finSpeed
    littleFish2.leftFin.rotation.y += 0.2*finSpeed
    littleFish2.rightFin.rotation.y -= 0.2*finSpeed
    littleFish3.tailFin.rotation.x += 0.34*finSpeed
    littleFish3.leftFin.rotation.y += 0.23*finSpeed
    littleFish3.rightFin.rotation.y -= 0.23*finSpeed
  }

  function animateTurtle(flipperSpeed, fast) {
    turtle.frontRight.rotation.x -= flipperSpeed/(fast ? 1 : 8)
      turtle.frontLeft.rotation.x += flipperSpeed/(fast ? 1 : 8)
      turtle.backRight.rotation.x += 0.4*flipperSpeed/(fast ? 1 : 8)
      turtle.backLeft.rotation.x -= 0.4*flipperSpeed/(fast ? 1 : 8)
  }

  function animateKelp() {
    const t = clock.getElapsedTime()
    for (const seakelp of coreInhabitants.kelpgroup.children) {
      for (const kelp of seakelp.children) {
        kelp.geometry.vertices.map(v => {
          v.z = 0.5 * Math.sin(v.x * 200 + t*2)
        })
        kelp.geometry.verticesNeedUpdate = true
      }
    }
  }

  function animateJellyfish() {
    tentacleSway += rotateModifier/10
    let i = 0;
    for (const tentacle of jellyFish.tentacles.children) {
      let initialY = tentacle.geometry.vertices[tentacle.geometry.vertices.length - 1].y
      tentacle.geometry.vertices.map(v => {
        v.x = (initialY - v.y) * rotateModifier/6 * Math.sin(v.y/5 + tentacleSway + i)
      })
      tentacle.geometry.verticesNeedUpdate = true
      i++
    }
  }

  render()

  const animate = () => {
    window.requestAnimationFrame(animate)
    window.addEventListener('click', onDocumentMouseDown, false)

    if (counter < 2/FIN_SPEED) {
      animateFish(FIN_SPEED)
    } else {
      animateFish(-FIN_SPEED)
    }

    if (longCounter < 80) {
      coreInhabitants.clam.clamTop.rotation.z += 0.02
    } else if (longCounter >= 82) {
      coreInhabitants.clam.clamTop.rotation.z = 0.17
    } else {
      coreInhabitants.clam.clamTop.rotation.z -= 0.8
    }
    
    if (longCounter < 120) {
      animateTurtle(0.05, false)
      rotateModifier *= 0.992
    } else {
      animateTurtle(-0.05, true)
      rotateModifier += 0.1237
    }

    animateKelp()
    animateJellyfish()

    if (turning) {
      coreInhabitants.group.rotation.x += DEFAULT_ROTATION_RATE
      coreInhabitants.group.rotation.y += DEFAULT_ROTATION_RATE
      particles.group.rotation.y -= DEFAULT_ROTATION_RATE * 0.9
      turtleRotation.rotation.y -= DEFAULT_ROTATION_RATE * rotateModifier
      jellyRotation.rotation.x += DEFAULT_ROTATION_RATE * rotateModifier
      fishRotation.rotation.y += DEFAULT_ROTATION_RATE
      littleFishRotation.rotation.y += DEFAULT_ROTATION_RATE *1.1
    }
  }

  function onDocumentMouseDown(event) {
    event.preventDefault()
    mouse.x = (event.clientX / renderer.domElement.clientWidth) * 2 - 1
    mouse.y = -(event.clientY / renderer.domElement.clientHeight) * 2 + 1

    raycaster.setFromCamera(mouse, camera)
    let intersects = raycaster.intersectObjects(
      particles.group.children.flatMap(x => (x.hasOwnProperty('children') && x.children.length > 0 ? x.children : x))
    )

    if (intersects.length > 0) {
      if (intersects[0].object.hasOwnProperty('callback')) {
        intersects[0].object.callback()
      } else if (intersects[0].object.parent.hasOwnProperty('callback')) {
        intersects[0].object.parent.callback()
      }
    }
  }

  return {
    camera,
    renderer,
    animate,
    turn,
    stop,
    cast: [
      coreInhabitants,
      particles,
      turtle,
      jellyFish,
      fish,
      littleFish1,
      littleFish2, 
      littleFish3
    ]
  }
}

export { createDemoUniverse }
